import {
  useGetFailedEmployeeImportJobsQuery,
  useGetFailedEmployeeImportRecordsQuery,
  useLazyDownloadEmployeeExcelQuery,
  useUploadEmployeeExcelMutation,
} from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import type { FailedImportRecordV2 } from '@/app/features/employee/emp-dashboard/emp-dashboard.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  data: any;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedImportRecordV2>['columns'] = [
  {
    title: 'Name',
    dataIndex: 'name',
    render: (val) =>
      val ? <span className="block min-w-[100px]">{val}</span> : 'n/a',
  },
  {
    title: 'ID',
    dataIndex: 'employee_id',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Department',
    dataIndex: 'department_name',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Designation',
    dataIndex: 'designation_name',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Joining Date',
    dataIndex: 'joining_date',
    render: (val) =>
      val ? <span className="block min-w-[80px]">{val}</span> : 'n/a',
  },
  {
    title: 'Employee Type',
    dataIndex: 'employee_type',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Salary',
    dataIndex: 'salary',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function BulkEmployeeUpload({ open, handleClose }: Props) {
  const [uploadEmployeeExcel, { isLoading: isUploading }] =
    useUploadEmployeeExcelMutation();
  const [downloadTemplate] = useLazyDownloadEmployeeExcelQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      console.log(blobUrl);
      link.href = blobUrl;
      link.download = 'employee-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upsert_if_exists', 'false');
    await uploadEmployeeExcel(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Employee CSV Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      {/* Employee Upload Error list Drawer */}
      <BulkUploadErrors<FailedImportRecordV2>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedEmployeeImportJobsQuery}
        useGetRecordsQuery={useGetFailedEmployeeImportRecordsQuery}
        jobsFieldMap={{ createdAt: 'attemptedAt' }}
        columns={columns}
        jobsErrorMessage="Failed to get employee import error data"
        recordsErrorMessage="Failed to get employee import error data"
      />
    </>
  );
}
